import { useData } from 'vike-react/useData';
import { ArrowLeft, Calendar, Clock, User, BookOpen } from 'lucide-react';
import HeaderSSR from '../../../components/HeaderSSR';
import FooterSSR from '../../../components/FooterSSR';
import { transformAccordionHtml } from '../../../lib/utils/transformAccordionHtml';
import { getGuideHeroSrc, getGuideHeroSrcSet } from '../../../../utils/imageUrl';
import type { GuidePageData } from './+data';
import MobileAppPrompt from '../../../components/MobileAppPrompt';

function getReadingTime(content: string | null): string {
  if (!content) return '3 min read';
  const text = content.replace(/<[^>]*>/g, '');
  const words = text.trim().split(/\s+/).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
}

export default function Page() {
  const { guide, otherGuides } = useData<GuidePageData>();

  const publishedDate = guide.published_at || guide.created_at;

  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderSSR />

      <article className="pt-16">
        {/* Hero / Featured Image (LCP element) */}
        {guide.featured_image && (
          <div className="w-full h-64 md:h-96 bg-gray-200">
            <img
              src={getGuideHeroSrc(guide.featured_image)}
              srcSet={getGuideHeroSrcSet(guide.featured_image)}
              sizes="100vw"
              alt={guide.title}
              className="w-full h-full object-cover"
              // Hero is the LCP element: load eagerly with high priority.
              // Must match the <link rel="preload"> in +data.tsx exactly.
              fetchPriority="high"
              decoding="async"
            />
          </div>
        )}

        <div className="max-w-3xl mx-auto px-4 py-8">
          {/* Back link */}
          <a
            href="/guides"
            className="inline-flex items-center gap-2 text-[#134025] hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Guides
          </a>

          {/* Category */}
          {guide.category && (
            <span className="inline-block bg-[#134025]/10 text-[#134025] text-sm px-3 py-1 rounded-full mb-4">
              {guide.category}
            </span>
          )}

          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {guide.title}
          </h1>

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm mb-8">
            {guide.author && (
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {guide.author}
              </span>
            )}
            {publishedDate && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(publishedDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {getReadingTime(guide.content)}
            </span>
          </div>

          {/* Description */}
          {guide.description && (
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              {guide.description}
            </p>
          )}

          {/* Content */}
          {guide.content && (
            <div
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-a:text-[#134025]
                prose-p:mb-6 prose-p:leading-relaxed
                prose-headings:mt-10 prose-headings:mb-4
                prose-h2:text-2xl prose-h2:font-bold prose-h2:mt-12
                prose-h3:text-xl prose-h3:font-semibold prose-h3:mt-8
                prose-ul:my-6 prose-ul:pl-6 prose-ol:my-6 prose-ol:pl-6 prose-li:mb-2
                prose-blockquote:my-6 prose-blockquote:pl-4 prose-blockquote:border-l-4 prose-blockquote:border-[#134025]
                [&>*]:mb-6
                [&_table]:w-full [&_table]:border-collapse [&_table]:my-8
                [&_td]:border [&_td]:border-gray-300 [&_td]:px-4 [&_td]:py-3
                [&_th]:border [&_th]:border-gray-300 [&_th]:px-4 [&_th]:py-3 [&_th]:bg-gray-50 [&_th]:font-semibold
                [&_tr:nth-child(even)]:bg-gray-50/50"
              dangerouslySetInnerHTML={{ __html: transformAccordionHtml(guide.content) }}
            />
          )}
        </div>

        {/* Other Guides */}
        {otherGuides.length > 0 && (
          <section className="max-w-7xl mx-auto px-6 py-12 border-t border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              Other Guides
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherGuides.slice(0, 3).map((otherGuide) => (
                <a
                  key={otherGuide.id}
                  href={`/guides/${otherGuide.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="overflow-hidden aspect-video relative">
                    {otherGuide.featured_image ? (
                      <img
                        src={otherGuide.featured_image}
                        alt={otherGuide.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#134025]/20 to-[#134025]/5 flex items-center justify-center">
                        <BookOpen className="w-8 h-8 text-[#134025]/30" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    {otherGuide.category && (
                      <span className="text-xs uppercase tracking-wider text-[#134025] font-medium">
                        {otherGuide.category}
                      </span>
                    )}
                    <h3 className="font-semibold text-gray-900 mt-1 line-clamp-2 group-hover:text-[#134025] transition-colors duration-200">
                      {otherGuide.title}
                    </h3>
                    {otherGuide.description && (
                      <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                        {otherGuide.description}
                      </p>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}
      </article>

      {/* =============================================
          GROWTH-01 — WEB-TO-APP PROMPT (mobile web only)
          Guides are where most of our mobile search traffic lands, so this
          is the highest-volume placement of the three. Client-only: nothing
          in this page's SSR HTML, nothing on the first client render.

          There is no sticky CTA and no BottomNav on this route, so the
          banner measures nothing and sits at its base safe-area inset.

          The CTA is a plain Play Store link on Android rather than an
          intent URL: the app does not claim /guides/* (AndroidManifest,
          the AASA and lib/deepLinkRoutes.ts all stop at /property/ and
          /syndicated/), so an intent would resolve to no activity and fall
          through to the same store page anyway. appCtaHref reads that
          allowlist, so claiming /guides/ in the app is the only change
          needed to upgrade this to a real deep link.
          ============================================= */}
      <MobileAppPrompt pageType="guide" contentId={guide.slug} />

      <FooterSSR />
    </div>
  );
}
